/* Cloud session history — server-side history per logged-in user.
 *
 * UX:
 *   - "Cloud save" button (☁️ ↑): prompts for a name, saves the current
 *     snapshot to lopettia-auth as a UserBlob(product='aiboard', kind='session').
 *   - "Cloud history" button (☁️): opens a modal showing all saved sessions
 *     newest first (pinned first), with Load / Delete actions. Includes a
 *     quota strip at the top.
 *   - If the user is not signed in: both buttons show a friendly toast
 *     telling them to log in to enable cloud history.
 *
 * The actual snapshot logic (capture / apply / validate) lives in
 * session-io.ts — this module is the cloud transport + UI shell.
 *
 * Replicable to La99 or other Lopettia products: copy this file + change
 * the kind constant (e.g. 'session' → 'brief') and the snapshot helpers.
 */

import { captureSnapshot, applySnapshot, isValidSnapshot } from './session-io';
import { getUser, subscribeUser, type User } from '../auth';
import { listBlobs, saveBlob, getBlob, updateBlob, deleteBlob, BlobApiError, type BlobMeta } from './cloud-blobs';
import { toast } from './toast';
import { t } from './i18n';
import { escapeHtml } from './safe-dom';  // F-17 consolidation

const KIND = 'session';

// ============================================================================
// Save flow
// ============================================================================

function defaultName(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Session · ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export async function cloudSaveSession(): Promise<void> {
  const user = getUser();
  if (!user) {
    toast(t('session.cloud_login_required') || 'Sign in to save sessions to the cloud.');
    return;
  }
  const snap = captureSnapshot();
  if (!snap.prompt && snap.responses.length === 0) {
    toast(t('session.empty') || 'Nothing to save yet — write a prompt or run a comparison first.');
    return;
  }

  const suggested = (snap.prompt || '').trim().slice(0, 60) || defaultName();
  const name = window.prompt(t('session.cloud_save_name') || 'Save this session as:', suggested);
  if (!name) return;

  try {
    await saveBlob(KIND, name.trim(), snap as unknown as Record<string, unknown>);
    toast(t('session.cloud_saved') || `Saved to cloud: ${name}`);
  } catch (err) {
    if (err instanceof BlobApiError && err.isQuotaExceeded) {
      toast(t('session.cloud_quota') || 'Storage quota reached. Upgrade to keep saving cloud sessions.');
      return;
    }
    if (err instanceof BlobApiError && err.isUnauthorized) {
      toast(t('session.cloud_login_required') || 'Sign in to save sessions to the cloud.');
      return;
    }
    console.error('cloud save failed', err);
    toast('Cloud save failed: ' + (err as Error).message);
  }
}

// ============================================================================
// History modal — list / load / delete
// ============================================================================

const MODAL_ID = 'cloud-sessions-modal';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}

function buildModal(): HTMLElement {
  let m = document.getElementById(MODAL_ID);
  if (m) return m;
  m = document.createElement('div');
  m.id = MODAL_ID;
  m.className = 'cloud-modal';
  m.hidden = true;
  m.innerHTML = `
    <div class="cloud-modal-backdrop" data-close="1"></div>
    <div class="cloud-modal-card" role="dialog" aria-modal="true" aria-labelledby="cloud-modal-title">
      <header class="cloud-modal-head">
        <div>
          <span class="cloud-modal-eyebrow">CLOUD SESSIONS</span>
          <h2 id="cloud-modal-title" class="cloud-modal-title">Your saved sessions</h2>
        </div>
        <button class="cloud-modal-close" type="button" aria-label="Close" data-close="1">×</button>
      </header>
      <div class="cloud-modal-quota" id="cloud-modal-quota"></div>
      <div class="cloud-modal-body" id="cloud-modal-body">
        <p class="cloud-modal-empty">Loading…</p>
      </div>
    </div>
  `;
  document.body.appendChild(m);
  m.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close === '1') closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !m!.hidden) closeModal();
  });
  return m;
}

function closeModal(): void {
  const m = document.getElementById(MODAL_ID);
  if (m) m.hidden = true;
}

async function refreshModal(): Promise<void> {
  const body = document.getElementById('cloud-modal-body');
  const quotaEl = document.getElementById('cloud-modal-quota');
  if (!body || !quotaEl) return;
  body.innerHTML = '<p class="cloud-modal-empty">Loading…</p>';
  quotaEl.innerHTML = '';
  try {
    const res = await listBlobs(KIND, { limit: 100 });
    const { quota, blobs } = res;
    const usedPct = (quota.used_bytes / quota.max_bytes) * 100;
    quotaEl.innerHTML = `
      <div class="cloud-quota-row">
        <span class="cloud-quota-text">${quota.used_blobs} / ${quota.max_blobs} sessions ·
        ${fmtBytes(quota.used_bytes)} / ${fmtBytes(quota.max_bytes)}</span>
      </div>
      <div class="cloud-quota-bar">
        <div class="cloud-quota-fill" style="width:${Math.min(100, usedPct).toFixed(1)}%"></div>
      </div>
    `;
    if (!blobs.length) {
      body.innerHTML = `<p class="cloud-modal-empty">${t('session.cloud_empty') || 'No saved sessions yet. Click "☁️ Save" while working to save one.'}</p>`;
      return;
    }
    body.innerHTML = `<ul class="cloud-list">${blobs.map(rowHtml).join('')}</ul>`;
    body.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((btn) => {
      btn.addEventListener('click', onRowAction);
    });
  } catch (err) {
    if (err instanceof BlobApiError && err.isUnauthorized) {
      body.innerHTML = `<p class="cloud-modal-empty">${t('session.cloud_login_required') || 'Sign in to view cloud sessions.'}</p>`;
      return;
    }
    body.innerHTML = `<p class="cloud-modal-empty cloud-modal-error">${escapeHtml((err as Error).message)}</p>`;
  }
}

function rowHtml(b: BlobMeta): string {
  const pinTitle = b.is_pinned ? 'Unpin' : 'Pin';
  const pinIcon  = b.is_pinned ? '★' : '☆';
  return `
    <li class="cloud-row" data-id="${b.id}" data-name="${escapeHtml(b.name)}" data-pinned="${b.is_pinned ? '1' : '0'}">
      <div class="cloud-row-main">
        <div class="cloud-row-name">${escapeHtml(b.name)}</div>
        <div class="cloud-row-meta">${fmtWhen(b.updated_at)} · ${fmtBytes(b.size_bytes)}</div>
      </div>
      <div class="cloud-row-actions">
        <button class="cloud-row-btn cloud-row-btn-pin" type="button" data-action="pin" title="${pinTitle}" aria-label="${pinTitle}">${pinIcon}</button>
        <button class="cloud-row-btn" type="button" data-action="rename" title="Rename">Rename</button>
        <button class="cloud-row-btn cloud-row-btn-load"   type="button" data-action="load">Load</button>
        <button class="cloud-row-btn cloud-row-btn-delete" type="button" data-action="delete">Delete</button>
      </div>
    </li>
  `;
}

async function onRowAction(e: Event): Promise<void> {
  const btn = e.currentTarget as HTMLButtonElement;
  const row = btn.closest('.cloud-row') as HTMLElement | null;
  if (!row) return;
  const id = row.dataset.id!;
  const name = row.dataset.name!;
  const wasPinned = row.dataset.pinned === '1';
  const action = btn.dataset.action;

  if (action === 'load') {
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const blob = await getBlob(id);
      if (!isValidSnapshot(blob.content)) {
        toast(t('session.invalid') || 'That cloud session is not in a recognizable format.');
        return;
      }
      applySnapshot(blob.content as any);
      toast(`${t('session.loaded') || 'Session loaded'} · ${name}`);
      closeModal();
    } catch (err) {
      toast('Load failed: ' + (err as Error).message);
      btn.disabled = false;
      btn.textContent = 'Load';
    }
    return;
  }

  if (action === 'rename') {
    const newName = window.prompt('Rename this cloud session to:', name);
    if (!newName || newName.trim() === name) return;
    btn.disabled = true;
    try {
      await updateBlob(id, { name: newName.trim() });
      toast(`Renamed to: ${newName.trim()}`);
      await refreshModal();
    } catch (err) {
      if (err instanceof BlobApiError && err.status === 409) {
        toast(`Name already taken: ${newName.trim()}`);
      } else {
        toast('Rename failed: ' + (err as Error).message);
      }
      btn.disabled = false;
    }
    return;
  }

  if (action === 'pin') {
    btn.disabled = true;
    try {
      await updateBlob(id, { is_pinned: !wasPinned });
      await refreshModal();
    } catch (err) {
      toast('Pin failed: ' + (err as Error).message);
      btn.disabled = false;
    }
    return;
  }

  if (action === 'delete') {
    if (!window.confirm(`Delete cloud session "${name}"? This cannot be undone.`)) return;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      await deleteBlob(id);
      toast(`Deleted: ${name}`);
      await refreshModal();
    } catch (err) {
      toast('Delete failed: ' + (err as Error).message);
      btn.disabled = false;
      btn.textContent = 'Delete';
    }
  }
}

export async function openCloudHistory(): Promise<void> {
  if (!getUser()) {
    toast(t('session.cloud_login_required') || 'Sign in to view cloud sessions.');
    return;
  }
  const m = buildModal();
  m.hidden = false;
  await refreshModal();
}

// ============================================================================
// Wiring
// ============================================================================

function syncButtons(u: User | null): void {
  // Show the cloud buttons only when the user is signed in. They live in the
  // prompt panel header next to the local Save/Open buttons.
  ['session-cloud-save-btn', 'session-cloud-history-btn'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.style.display = u ? '' : 'none';
  });
}

export function attachCloudSessionsHandlers(): void {
  document.getElementById('session-cloud-save-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    cloudSaveSession();
  });
  document.getElementById('session-cloud-history-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    openCloudHistory();
  });
  // Toggle visibility on auth state changes
  syncButtons(getUser());
  subscribeUser(syncButtons);
}

if (typeof window !== 'undefined') {
  (window as any).aiBoardCloudSave = cloudSaveSession;
  (window as any).aiBoardCloudHistory = openCloudHistory;
}
